import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, RefreshCw, X } from "lucide-react";
import { apiFetch } from "@shared/lib/api";
import { useAuth } from "@shared/lib/auth";
import { useToast } from "@shared/lib/toast";
import type { ComplianceAlert } from "@shared/lib/types";
import { useNavigate } from "react-router-dom";

type AlertsResponse = {
  alerts: ComplianceAlert[];
  total_count: number;
  blocking_count: number;
  certification_alert_count: number;
};

function getAlertSubjectName(alert: ComplianceAlert): string {
  // For staff certification alerts, show the staff member's name
  if (alert.is_staff_alert && alert.user) {
    return `Staff: ${alert.user.full_name || alert.user.email}`;
  }
  // For child-related alerts, show the child's name
  if (alert.child) {
    return `${alert.child.first_name} ${alert.child.last_name}`;
  }
  return "Alert";
}

const BANNER_DISMISSED_KEY = "compliance_banner_dismissed";

export function ComplianceBanner() {
  const auth = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [alerts, setAlerts] = useState<ComplianceAlert[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem(BANNER_DISMISSED_KEY) === "true";
  });

  const fetchAlerts = useCallback(async () => {
    if (auth.status !== "authenticated") return;

    try {
      const res = await apiFetch<AlertsResponse>("/api/v1/compliance_alerts");
      setAlerts(res.data.alerts);
    } catch {
      // Silently fail - banner is non-critical UI
    } finally {
      setLoading(false);
    }
  }, [auth.status]);

  useEffect(() => {
    void fetchAlerts();
    // Poll every 5 minutes for new alerts
    const interval = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  if (loading || alerts.length === 0 || dismissed) return null;

  const currentAlert = alerts[currentIndex];
  const hasMultiple = alerts.length > 1;

  const goNext = () => setCurrentIndex((i) => (i + 1) % alerts.length);
  const goPrev = () => setCurrentIndex((i) => (i - 1 + alerts.length) % alerts.length);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAlerts();
    setRefreshing(false);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    sessionStorage.setItem(BANNER_DISMISSED_KEY, "true");
    setDismissed(true);
    toast.info("Dismissed", "View alerts in History.");
  };

  const handleClick = () => {
    if (currentAlert.navigation_url) {
      navigate(currentAlert.navigation_url);
    }
  };

  const severityColors: Record<string, string> = {
    critical: "bg-danger-bg text-danger-foreground border-l-4 border-danger",
    high: "bg-danger-bg text-danger-foreground border-l-4 border-danger/60",
    medium: "bg-warning-bg text-warning-text border-l-4 border-warning-text",
    low: "bg-info-bg text-info-text border-l-4 border-info-text",
  };

  const bgColor = severityColors[currentAlert.severity] || severityColors.medium;

  return (
    <div className={`${bgColor} px-4 py-3 border-b border-border`}>
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        {hasMultiple && (
          <button
            onClick={goPrev}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-foreground/10"
            aria-label="Previous alert"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={handleClick}
          className="flex min-w-0 flex-1 items-center gap-3 text-left hover:opacity-90"
        >
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-semibold">{getAlertSubjectName(currentAlert)}</div>
            <div className="truncate text-sm opacity-90">{currentAlert.message}</div>
            {currentAlert.days_until_deadline != null && currentAlert.days_until_deadline <= 7 && (
              <div className="mt-1 text-xs font-medium opacity-80">
                {currentAlert.days_until_deadline <= 0
                  ? "OVERDUE"
                  : `${currentAlert.days_until_deadline} day${currentAlert.days_until_deadline !== 1 ? "s" : ""} remaining`}
              </div>
            )}
          </div>
        </button>

        {hasMultiple && (
          <div className="shrink-0 text-sm font-medium opacity-80">
            {currentIndex + 1} / {alerts.length}
          </div>
        )}

        {hasMultiple && (
          <button
            onClick={goNext}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-foreground/10"
            aria-label="Next alert"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-foreground/10 disabled:opacity-50"
          aria-label="Refresh alerts"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>

        <button
          onClick={handleDismiss}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-foreground/10"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
